import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '../lib/queryKeys';
import { supabase } from '../lib/supabase';
import { useCurrentCompany } from './useCurrentCompany';

export interface OnboardingMetrics {
  uploaded: number; // Draft students uploaded but not yet invited
  invited: number; // Invites sent but not yet accepted
  accepted: number; // Invites accepted but not yet registered (user_id still null)
}

/**
 * Hook to fetch onboarding metrics for draft students
 * Returns counts for: uploaded, invited, and accepted draft students
 */
export function useOnboardingMetrics() {
  const { data: companyId, isLoading: isLoadingCompany } = useCurrentCompany();

  return useQuery<OnboardingMetrics>({
    queryKey: queryKeys.dashboard.onboardingMetrics(companyId),
    enabled: !!companyId && !isLoadingCompany,
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true,
    queryFn: async () => {
      if (!companyId) {
        return { uploaded: 0, invited: 0, accepted: 0 };
      }

      // Uploaded: draft students that haven't been invited yet
      const { count: uploadedCount } = await supabase
        .from('draft_students_view')
        .select('id', { count: 'exact', head: true })
        .eq('company_id', companyId)
        .is('invite_sent_at', null)
        .is('user_id', null);

      // Invited: invites sent but not yet accepted
      const { count: invitedCount } = await supabase
        .from('draft_students_view')
        .select('id', { count: 'exact', head: true })
        .eq('company_id', companyId)
        .not('invite_sent_at', 'is', null)
        .is('invite_accepted_at', null)
        .is('user_id', null);

      // Accepted: invites accepted but not yet registered (user_id still null)
      const { count: acceptedCount } = await supabase
        .from('draft_students_view')
        .select('id', { count: 'exact', head: true })
        .eq('company_id', companyId)
        .not('invite_accepted_at', 'is', null)
        .is('user_id', null);

      return {
        uploaded: uploadedCount || 0,
        invited: invitedCount || 0,
        accepted: acceptedCount || 0,
      };
    },
  });
}
